import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
  ApiEnvelope, AuthToken, Customer, OtpSent, OtpVerified, PhoneCheck,
} from '../models/auth.model';

const CC = '20'; // Egypt (only supported country code per the API)

/**
 * Sonic storefront authentication.
 *
 * Phone-first flow:
 *   checkPhone → (login | register/send-otp → register/verify-otp → register)
 *   forgot: password/forgot → password/verify-otp → password/reset
 *
 * The `X-Brand-Subdomain` / `Authorization` headers are attached by AuthInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly base = environment.apiBase;

  constructor(private http: HttpClient) {}

  /** Step 1: where to route this phone number (login vs register). */
  checkPhone(phone: string): Observable<PhoneCheck> {
    return this.post('/api/v1/auth/phone/check', { country_code: CC, phone_number: phone });
  }

  /** Returning user: sign in with password. */
  login(phone: string, password: string): Observable<AuthToken> {
    return this.post('/api/v1/auth/login', { country_code: CC, phone_number: phone, password });
  }

  /** Validate the current token / hydrate the profile. */
  me(): Observable<Customer> {
    return this.get('/api/v1/auth/me');
  }

  // ── registration ──
  registerSendOtp(phone: string): Observable<OtpSent> {
    return this.post('/api/v1/auth/register/send-otp', { country_code: CC, phone_number: phone, channel: 'sms' });
  }
  registerVerifyOtp(phone: string, otp: string): Observable<OtpVerified> {
    return this.post('/api/v1/auth/register/verify-otp', { country_code: CC, phone_number: phone, otp });
  }
  register(phone: string, registrationToken: string, name: string, password: string, email?: string): Observable<AuthToken> {
    const body: Record<string, unknown> = {
      country_code: CC, phone_number: phone, registration_token: registrationToken, name, password,
    };
    if (email) body['email'] = email;
    return this.post('/api/v1/auth/register', body);
  }

  // ── forgot / reset password ──
  forgotPassword(phone: string): Observable<OtpSent> {
    return this.post('/api/v1/auth/password/forgot', { country_code: CC, phone_number: phone, channel: 'sms' });
  }
  passwordVerifyOtp(phone: string, otp: string): Observable<OtpVerified> {
    return this.post('/api/v1/auth/password/verify-otp', { country_code: CC, phone_number: phone, otp });
  }
  passwordReset(phone: string, resetToken: string, password: string): Observable<null> {
    return this.post('/api/v1/auth/password/reset', { country_code: CC, phone_number: phone, reset_token: resetToken, password });
  }

  // ── helpers (unwrap the { success, data, message } envelope) ──
  private post<T>(path: string, body: unknown): Observable<T> {
    return this.http.post<ApiEnvelope<T>>(this.base + path, body).pipe(map((r) => r.data));
  }
  private get<T>(path: string): Observable<T> {
    return this.http.get<ApiEnvelope<T>>(this.base + path).pipe(map((r) => r.data));
  }
}
